import loadBlogList from "@iewccouk/Store/Handlers/Blog/LoadBlogList";
import { CustomState } from "@iewccouk/Store/Reducers";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import MobiusPage from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    description = "description",

    displayDescription = "Page description",
}

interface OwnProps extends PageProps {
    fields: {
        [fields.description]: string;
    };
}

const mapStateToProps = (customState: CustomState) => {
    const getPage = getCurrentPage(customState);
    const location = getLocation(customState);

    const pageId = getPage.nodeId;
    const { isLoading, parameters, filterQuery } = customState.blogList;

    return {
        pageId,
        location,
        isLoading,
        parameters,
        filterQuery,
    };
};

const mapDispatchToProps = {
    loadBlogList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const BlogCategoryPage = ({
    id,
    pageId,
    location: { search, pathname },
    isLoading,
    parameters,
    filterQuery,
    history,
    loadBlogList,
}: Props) => {
    const [isFirstLoad, setIsFirstLoad] = React.useState(true);

    React.useEffect(() => {
        loadBlogList({ pageId, search, isLandingPage: false, parameters, isFirstLoad: true });
        setIsFirstLoad(false);
    }, []);

    React.useEffect(() => {
        if (!isLoading && filterQuery && !isFirstLoad) {
            const currentUrl = `${pathname}${search}`;
            const newUrl = filterQuery ? `${pathname}?${filterQuery}` : pathname;
            if (newUrl !== currentUrl) {
                history.push(newUrl);
                loadBlogList({ pageId, search: filterQuery, isLandingPage: false, parameters, isFirstLoad: false });
            }
        }
    }, [filterQuery]);

    return (
        <MobiusPage>
            <Zone
                contentId={id as never}
                zoneName={"Content" as never}
                requireRows={true as never}
                fixed={true as never}
            />
        </MobiusPage>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(BlogCategoryPage) as any) as any,
    definition: {
        displayName: "Blog Category",
        pageType: "Content",
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        allowedParents: ["BlogLandingPage", "BlogCategoryPage"],
        allowedChildren: ["BlogCategoryPage", "BlogDetailPage"],
        fieldDefinitions: [
            {
                name: fields.description,
                displayName: fields.displayDescription,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
                collapsedToolbarButtons: {
                    moreText: {
                        buttons: ["paragraphFormat", "bold", "italic", "html"],
                        buttonsVisible: 4,
                    },
                },
            },
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
